<template>
  <ResourceDialog
    @close="handleClose"
    ref="dialog"
    :max-width="600"
    cancel-button-text="Close"
    content-test-id="public-record-detail-dialog"
    title="Public record"
    closeable
    hide-save-button
  >
    <template #form>
      <div
        v-if="publicRecord"
        class="d-flex flex-column ga-3"
      >
        <v-row
          v-if="canView('id') || canView('schema_id')"
          dense
          no-gutters
        >
          <LabeledControl
            v-if="canView('id')"
            :cols="canView('schema_id') ? 6 : 12"
            message="ID"
          >
            <span data-testid="public-record-detail-id">{{ shortId(publicRecord.id) }}</span>
          </LabeledControl>

          <LabeledControl
            v-if="canView('schema_id')"
            :cols="canView('id') ? 6 : 12"
            message="Type"
          >
            <span data-testid="public-record-detail-type">{{ publicRecordTypeName }}</span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('published_date') || canView('severity')"
          dense
          no-gutters
        >
          <LabeledControl
            v-if="canView('published_date')"
            :cols="canView('severity') ? 6 : 12"
            message="Published"
          >
            <StandardChip
              :active="isPublicRecordPublished(publicRecord)"
              data-testid="public-record-detail-published"
              role="publishedChip"
              size="small"
            />
          </LabeledControl>

          <LabeledControl
            v-if="canView('severity')"
            :cols="canView('published_date') ? 6 : 12"
            message="Severity"
          >
            <span data-testid="public-record-detail-severity">
              {{ publicRecord.severity || blankValue }}
            </span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('published_date') || canView('event_date')"
          dense
          no-gutters
        >
          <LabeledControl
            v-if="canView('published_date')"
            :cols="canView('event_date') ? 6 : 12"
            message="Published date"
          >
            <LongDate
              v-if="publicRecord.published_date"
              :date="publicRecord.published_date"
              data-testid="public-record-detail-published-date"
              date-classes="fs-16"
            />

            <span
              v-else
              data-testid="public-record-detail-published-date"
            >
              {{ blankValue }}
            </span>
          </LabeledControl>

          <LabeledControl
            v-if="canView('event_date')"
            :cols="canView('published_date') ? 6 : 12"
            message="Event date"
          >
            <LongDate
              v-if="publicRecord.event_date"
              :date="publicRecord.event_date"
              data-testid="public-record-detail-event-date"
              date-classes="fs-16"
            />

            <span
              v-else
              data-testid="public-record-detail-event-date"
            >
              {{ blankValue }}
            </span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('expiration_date') || canView('resolved_date')"
          dense
          no-gutters
        >
          <LabeledControl
            v-if="canView('expiration_date')"
            :cols="canView('resolved_date') ? 6 : 12"
            message="Expiration date"
          >
            <LongDate
              v-if="publicRecord.expiration_date"
              :date="publicRecord.expiration_date"
              data-testid="public-record-detail-expiration-date"
              date-classes="fs-16"
            />

            <span
              v-else
              data-testid="public-record-detail-expiration-date"
            >
              {{ blankValue }}
            </span>
          </LabeledControl>

          <LabeledControl
            v-if="canView('resolved_date')"
            :cols="canView('expiration_date') ? 6 : 12"
            message="Resolved date"
          >
            <LongDate
              v-if="publicRecord.resolved_date"
              :date="publicRecord.resolved_date"
              data-testid="public-record-detail-resolved-date"
              date-classes="fs-16"
            />

            <span
              v-else
              data-testid="public-record-detail-resolved-date"
            >
              {{ blankValue }}
            </span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('summary')"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Summary"
          >
            <p
              class="text-wrap text-break ws-pre-line mb-0"
              data-testid="public-record-detail-summary"
            >
              {{ publicRecord.summary || blankValue }}
            </p>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('description') && publicRecord.description"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Description"
          >
            <p
              class="text-wrap text-break ws-pre-line mb-0"
              data-testid="public-record-detail-description"
            >
              {{ publicRecord.description }}
            </p>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="canView('resolution_notes') && publicRecord.resolution_notes"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Resolution notes"
          >
            <p
              class="text-wrap text-break ws-pre-line mb-0"
              data-testid="public-record-detail-resolution-notes"
            >
              {{ publicRecord.resolution_notes }}
            </p>
          </LabeledControl>
        </v-row>
      </div>
    </template>
  </ResourceDialog>
</template>

<script setup>
import { useStore } from 'vuex';

import LabeledControl from '@/shared/components/form/LabeledControl.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import { shortId } from '@/plugins/filters';
import useRole from '@/shared/composables/useRole';

const blankValue = '--';

const { role } = useRole();
const store = useStore();

const dialog = ref(null);
const publicRecord = ref(null);

defineExpose({ open });

const emit = defineEmits(['close']);

const schemas = computed(() => store.state.schemas ?? {});

const schemaDefinition = computed(() => {
  const schemaId = publicRecord.value?.schema_id;
  return schemas.value[schemaId]?.definition;
});

const publicRecordTypeName = computed(() => {
  if (!publicRecord.value) return null;
  if (publicRecord.value.schema_name) return publicRecord.value.schema_name;
  if (!publicRecord.value.schema_id) return null;

  return schemas.value[publicRecord.value.schema_id]?.name ?? 'Unknown schema';
});

function canView(field) {
  const viewRoles = schemaDefinition.value?.properties?.[field]?.viewRoles;
  if (!Array.isArray(viewRoles)) return true;

  return viewRoles.includes(role);
}

function isPublicRecordPublished(record) {
  return Boolean(record.published_date);
}

async function open(record) {
  publicRecord.value = record;
  await nextTick();
  dialog.value?.open();
}

function handleClose() {
  publicRecord.value = null;
  emit('close');
}
</script>
